export enum Collections {
    Users = "users",
    ProvisionedUserData = "provisionedUserData",
    Admins = "admins",
    Cars = "cars",
    Cards = "cards",
    Gates = "gates",
    Reservations = "reservations",
    PermanentReservations = "permanentReservations",
    Parking = "parking",
    Spots = "spots",
    Abuses = "abuses",
    Companies = "companies",
    SingleUseSignUpCodes = "singleUseSignUpCodes",
    EmailDestinations = "emailDestinations",
    Connections = "connections",
    Rides = "rides",
    /** @deprecated Deprecated in 2.0 **/
    RecurringRides = "recurringRides",
    /** @deprecated Deprecated in 2.0 **/
    RideExceptions = "recRidesExc",
    EventIds = "eventIds",
    OccupancyCache = "occupancyCache",
    Routes = "routes",
    RoutesCache = "routesCache",
    Matches = "matches",
    UserPrivate = "private",
    UserPrivateAccess = "privateAccess",
    UserContactData = "contactInfo",
    AdditionalData = "additionalData",
    VisitorsEmailData = "visitorsEmailData",
    LocalisedVisitorsEmailData = "localisedVisitorsEmailData",
    Fleet = "fleet",
    FleetCars = "cars",
    FleetReservations = "reservations",
    Integrations = "integrations",
    GateCooldowns = "gateCooldowns"
}

export class Language {
    constructor(
        public id: string,
        public title: string
    ) {}
}

export class Email {
    constructor(
        public destination: string,
        public subject: string,
        public body: string,
        public icalEvent?: string
    ) {}
}

export enum FcmTokenType {
    Garage = "fcmPfToken",
    Carpool = "fcmToken"
}

export class Notification {
    constructor(
        public title: string,
        public body: string,
        public token: string,
        public senderId: string,
        public tokenType: FcmTokenType
    ) {}
}

export class SlackNotification {
    constructor(
        public title: string,
        public body: string,
        public accessToken: string,
        public slackUserId: string
    ) {}
}

export class OccupancyData {
    constructor(
        public remainingSpots: number,
        public confirmedReservations: number,
        public pendingReservations: number
    ) {}
}

// Info about a reservation that was canceled or trimmed when enforcing "max one active reservation per user".
export interface ReplacedReservation {
    reservationId: string
    locationId: string
    action: "canceled" | "trimmed"
    // For "trimmed": the new interval of the reservation, in unix seconds.
    newStartTime?: number
    newEndTime?: number
}

export interface OverflowInfo {
    requestedLocationId: string
    requestedLocationTitle: string
    locationId: string
    locationTitle: string
}

export interface PlaceData {
    name: string
    latitude: number
    longitude: number
    placeId?: string
    locationId?: string
}

export class Place {
    name: string
    lat: number
    lng: number
    placeId?: string
    locationId?: string

    constructor(name: string, lat: number, lng: number, placeId?: string, locationId?: string) {
        const precision = 100000
        this.name = name
        this.lat = Math.round(lat * precision) / precision
        this.lng = Math.round(lng * precision) / precision
        this.placeId = placeId
        this.locationId = locationId
    }

    asWaypoint = () => {
        if (this.placeId !== undefined) {
            return {
                placeId: this.placeId
            }
        }
        return {
            location: {
                latLng: {
                    latitude: this.lat,
                    longitude: this.lng
                }
            }
        }
    }

    asData = () => {
        const dict: { [k: string]: any } = {
            name: this.name,
            latitude: this.lat,
            longitude: this.lng,
            placeId: this.placeId,
            locationId: this.locationId
        }
        return dict
    }

    asTypedData = (): PlaceData => ({
        name: this.name,
        latitude: this.lat,
        longitude: this.lng,
        placeId: this.placeId,
        locationId: this.locationId
    })

    static fromData(data: { [k: string]: any }) {
        if (data.name && data.latitude !== undefined && data.longitude !== undefined) {
            return new Place(data.name, data.latitude, data.longitude, data.placeId, data.locationId)
        }
        return undefined
    }
}

export enum Direction {
    Forward = 0,
    Backward = 1
}

export class RouteCache {
    timestampCreated: Date = new Date()

    constructor(
        public duration: number,
        public originLat: number,
        public originLng: number,
        public destLat: number,
        public destLng: number
    ) {}
}
